import { FC } from 'react';

import { Box, Flex, Heading, Text } from '@rover/kibble/core';
import { Badge } from '@rover/kibble/official-patterns/Badge';

import { BRIGHT_HORIZONS_CREDITS_DONT_APPLY } from '../../../callouts/CalloutBadge/CalloutBadge.constants';

import usePriceDisplayInfo, { PriceDisplayProps } from './usePriceDisplayInfo';

type Props = PriceDisplayProps & {
  // When the price-transparency "total" prefix is present, afterPriceText is
  // "total {unit}". Set this to render the prefix and the unit on separate lines
  // ("total" then "per night") instead of one line.
  stackTotalText?: boolean;
};

const LegacyPriceColumn: FC<Props> = ({ stackTotalText = false, ...props }) => {
  const { formattedPrice, beforePriceText, afterPriceText, showBrightHorizonsCalloutBadge } =
    usePriceDisplayInfo(props);

  // afterPriceText is either the bare unit ("per night") or "total {unit}". Derive the
  // "total" prefix by stripping the unit, so we can break after it without splitting on
  // whitespace (locale-safe).
  const totalText =
    afterPriceText !== props.unitAsSentence
      ? afterPriceText.replace(props.unitAsSentence, '').trim()
      : '';

  const afterPriceTextNode =
    stackTotalText && totalText ? (
      <>
        <Text size="100" textColor="tertiary" sx={{ whiteSpace: 'nowrap' }}>
          {totalText}
        </Text>
        <Text
          size="100"
          textColor="tertiary"
          sx={{ whiteSpace: 'nowrap' }}
          rxnProps={{ numberOfLines: 2, sx: { textAlign: 'right', width: '80px' } }}
        >
          {props.unitAsSentence}
        </Text>
      </>
    ) : (
      <Text
        size="100"
        textColor="tertiary"
        sx={{ whiteSpace: 'nowrap' }}
        rxnProps={{ numberOfLines: 2, sx: { textAlign: 'right', width: '80px' } }}
      >
        {afterPriceText}
      </Text>
    );

  return (
    <Flex
      flexDirection="column"
      alignItems="flex-end"
      alignSelf="stretch"
      justifyContent="flex-start"
      width="auto"
      maxWidth="100%"
    >
      {showBrightHorizonsCalloutBadge && (
        <Box marginBottom="1x" textAlign="right" maxWidth="150px">
          <Badge
            {...BRIGHT_HORIZONS_CREDITS_DONT_APPLY()}
            boxProps={{
              sx: {
                boxDecorationBreak: 'clone',
              },
            }}
          />
        </Box>
      )}
      <Flex flexDirection="column" alignItems="flex-end" gap="0.5x">
        {beforePriceText && (
          <Text size="100" textColor="tertiary">
            {beforePriceText}
          </Text>
        )}
        <Heading size="400" webProps={{ responsive: false, as: 'div' }} mb="0x" textColor="success">
          {formattedPrice}
        </Heading>
        {afterPriceTextNode}
      </Flex>
    </Flex>
  );
};

export default LegacyPriceColumn;
